/**
 * SectionConfigPage - 文字起こし→評価の間でセクション境界を確定する画面
 *
 * Pipeline ジョブが status=section_pending で停止した時に表示される。
 * Gemini 補正済み発話を取得 → ApproachA (2クリック方式) で割当 → 確定で評価開始。
 *
 * URL: /sessions/:sessionId/sections?jobId=...
 *  - jobId が無い場合は sessionId をそのまま pipeline job_id として扱う
 *  - 確定後 /sessions/:sessionId/status?jobId=:jobId へ遷移し ProcessingStatusPage が
 *    pipeline mode で続きをポーリング表示する
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LoadingState } from '@/components/Spinner';
import { getTranscription, submitSections } from '@/api/pipeline';
import { ApproachA } from './section-config/ApproachA';
import { getSectionsForDay } from './section-config/sectionDefinitions';
import type { SectionAssignment, SectionDefinition, TranscriptUtterance } from './section-config/types';

function formatTimestamp(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '00:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
  const s = String(totalSec % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

interface RawUtterance {
  id?: string;
  text?: string;
  speaker?: string;
  speaker_label?: string;
  speaker_name?: string;
  start_ms?: number;
  end_ms?: number;
}

function normalizeUtterances(raw: RawUtterance[]): TranscriptUtterance[] {
  return raw.map((u, i) => {
    const speakerLabel = String(u.speaker ?? u.speaker_label ?? '').toLowerCase();
    const isFacilitator =
      speakerLabel.includes('facilitator') ||
      speakerLabel.includes('司会') ||
      speakerLabel.startsWith('speaker_00') ||
      speakerLabel === 'speaker0' ||
      speakerLabel === 'speaker_0';
    // 内部技術ラベル（facilitator / participant / speaker_NN）は表示に使わず、
    // 日本語ラベル（コーチ/参加者）にフォールバックする。
    const isInternalLabel =
      speakerLabel === 'facilitator' ||
      speakerLabel === 'participant' ||
      /^speaker[_]?\d+$/.test(speakerLabel);
    const rawSpeaker = isInternalLabel ? '' : u.speaker;
    const speakerName = u.speaker_name || rawSpeaker || (isFacilitator ? 'コーチ' : '参加者');
    return {
      id: u.id ?? `u${i.toString().padStart(4, '0')}`,
      speaker: isFacilitator ? 'facilitator' : 'participant',
      speakerName,
      timestamp: formatTimestamp(u.start_ms ?? 0),
      text: u.text ?? '',
    };
  });
}

export function buildSectionsPayload(assignments: SectionAssignment[], sections: SectionDefinition[]) {
  const sectionById = new Map(sections.map(section => [section.id, section]));
  const seen = new Map<string, number>();
  return assignments.map(a => {
    const section = sectionById.get(a.sectionId);
    const occurrenceIndex = seen.get(a.sectionId) ?? 0;
    seen.set(a.sectionId, occurrenceIndex + 1);
    const isMulti = section?.allowMultipleRanges === true;
    const instanceOrder = occurrenceIndex;
    const instanceName = isMulti
      ? `${section?.name ?? a.sectionId} ${occurrenceIndex + 1}`
      : section?.name ?? a.sectionId;
    return {
      section_id: a.sectionId,
      start_utterance_index: a.startUtteranceIndex,
      end_utterance_index: a.endUtteranceIndex,
      section_instance_id: isMulti ? `${a.sectionId}__${occurrenceIndex + 1}` : a.sectionId,
      section_instance_name: instanceName,
      section_instance_order: instanceOrder,
    };
  });
}

export function SectionConfigPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const jobId = searchParams.get('jobId') || sessionId || '';
  const day = searchParams.get('day') || 'Day1';

  const [utterances, setUtterances] = useState<TranscriptUtterance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sections = useMemo(() => {
    return getSectionsForDay(day);
  }, [day]);

  useEffect(() => {
    let cancelled = false;
    if (!jobId) {
      setError('jobId が指定されていません');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    getTranscription(jobId)
      .then(data => {
        if (cancelled) return;
        const raw = (data as { utterances?: RawUtterance[] }).utterances || [];
        if (!Array.isArray(raw) || raw.length === 0) {
          setError('文字起こし結果が空です。Gemini 補正の完了を確認してください。');
          setUtterances([]);
        } else {
          setUtterances(normalizeUtterances(raw));
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const userMessage = (err as { userMessage?: string })?.userMessage;
        const fallback = err instanceof Error ? err.message : '文字起こし結果の取得に失敗しました';
        setError(userMessage || fallback);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const handleConfirm = async (assignments: SectionAssignment[]) => {
    if (!jobId || assignments.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const sectionsPayload = buildSectionsPayload(assignments, sections);
      await submitSections(jobId, sectionsPayload);
      navigate(`/sessions/${sessionId}/status?jobId=${encodeURIComponent(jobId)}`);
    } catch (err: unknown) {
      const userMessage = (err as { userMessage?: string })?.userMessage;
      const fallback = err instanceof Error ? err.message : 'セクション設定の送信に失敗しました';
      setSubmitError(userMessage || fallback);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] min-h-0">
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="mb-2">
          <Link to="/sessions" className="text-sm text-blue-600 hover:underline">
            ← セッション一覧
          </Link>
        </div>
        <h1 className="text-xl font-semibold text-gray-900">セクション設定</h1>
        <p className="mt-1 text-sm text-gray-500">
          {day} のセクション境界を割り当ててから「確定して評価へ進む」を押してください。
          {' '}Job ID: <span className="font-mono">{jobId}</span>
        </p>
        {submitError && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {submitError}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingState message="文字起こしを読み込み中..." />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-800">
            <p className="font-semibold">読み込みに失敗しました</p>
            <p className="mt-2">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
            >
              再読み込み
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-hidden">
          <ApproachA
            utterances={utterances}
            sections={sections}
            onConfirm={handleConfirm}
            isSubmitting={submitting}
          />
        </div>
      )}
    </div>
  );
}
